import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { OccEndpointsService } from '@spartacus/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CART_SIMULATION_NORMALIZER, ORDER_NORMALIZER } from '../../../features/checkout/base/connector/converters';
import {
  AvailableBuyersSearchParams,
  B2BComment,
  CartSimulation,
  Order,
  OrderApproval,
  OrderApprovalBuyerSearchResult,
  OrderApprovalDecision,
  OrderApprovalSearchParams,
  OrderApprovalSearchResult,
} from '../../model';
import { Occ } from '../../occ';
import { ConverterService } from '../../util';
import {
  B2B_COMMENTS_NORMALIZER,
  ORDER_APPROVALS_NORMALIZER,
  ORDER_APPROVAL_DECISION_NORMALIZER,
  ORDER_APPROVAL_DETAILS_NORMALIZER,
} from './converters';

@Injectable({
  providedIn: 'root',
})
export class OrderApprovalConnector {
  constructor(
    private httpClient: HttpClient,
    private occEndpointsService: OccEndpointsService,
    private converterService: ConverterService
  ) {}

  get(userId: string, orderNumber: string): Observable<Order> {
    return this.httpClient
      .get<Occ.Order>(
        this.occEndpointsService.buildUrl('orderApproval', {
          urlParams: {
            userId,
            orderNumber,
          },
        })
      )
      .pipe(this.converterService.pipeable(ORDER_NORMALIZER));
  }

  getPending(userId: string, orderNumber: string): Observable<CartSimulation> {
    return this.httpClient
      .get<Occ.CartSimulation>(
        this.occEndpointsService.buildUrl('pendingApproval', {
          urlParams: {
            userId,
            orderNumber,
          },
        })
      )
      .pipe(this.converterService.pipeable(CART_SIMULATION_NORMALIZER));
  }

  getB2BComments(userId: string, orderNumber: string, sapOrder: boolean): Observable<Array<B2BComment>> {
    return this.httpClient
      .get(
        this.occEndpointsService.buildUrl('b2bComments', {
          urlParams: {
            userId,
            orderNumber,
          },
          queryParams: { sapOrder },
        })
      )
      .pipe(
        map((data: any) => data.comments),
        this.converterService.pipeable(B2B_COMMENTS_NORMALIZER)
      );
  }

  getList(userId: string, searchParams: OrderApprovalSearchParams): Observable<OrderApprovalSearchResult> {
    return this.httpClient
      .get<OrderApprovalSearchResult>(
        this.occEndpointsService.buildUrl('orderApprovals', {
          urlParams: { userId },
          queryParams: {
            fields: 'FULL',
            page: searchParams.page ?? 0,
            count: searchParams.count ?? 10,
            sort: searchParams.sort,
            dateFrom: searchParams?.dateFrom,
            dateTo: searchParams?.dateTo,
            orderNumberOrId: searchParams?.searchText,
            approvalStatus: searchParams?.status,
            soldTo: searchParams?.soldTo,
            shipTo: searchParams?.shipTo,
            onlyReinvoicingOrders: searchParams?.onlyReinvoicingOrders,
          },
        })
      )
      .pipe(this.converterService.pipeable(ORDER_APPROVALS_NORMALIZER));
  }

  getAdminList(userId: string, searchParams: OrderApprovalSearchParams): Observable<OrderApprovalSearchResult> {
    return this.httpClient
      .get<OrderApprovalSearchResult>(
        this.occEndpointsService.buildUrl('orderApprovalsAdmin', {
          urlParams: { userId },
          queryParams: {
            fields: 'FULL',
            page: searchParams.page ?? 0,
            count: searchParams.count ?? 10,
            sort: searchParams.sort,
            dateFrom: searchParams?.dateFrom,
            dateTo: searchParams?.dateTo,
            orderNumberOrId: searchParams?.searchText,
            approvalStatus: searchParams?.status,
            roles: searchParams?.roles,
            soldTo: searchParams?.soldTo,
            shipTo: searchParams?.shipTo,
            buyer: searchParams?.buyer,
            approver: searchParams?.approver,
            assignedApprover: searchParams?.contactPerson,
            managedByCurrentUser: searchParams?.managedByCurrentUser,
            onlyReinvoicingOrders: searchParams?.onlyReinvoicingOrders,
          },
        })
      )
      .pipe(this.converterService.pipeable(ORDER_APPROVALS_NORMALIZER));
  }

  loadAvailableOrderApprovalBuyers(
    searchParams: AvailableBuyersSearchParams,
    userId: string
  ): Observable<OrderApprovalBuyerSearchResult> {
    const url = this.occEndpointsService.buildUrl('orderApprovalsBuyers', {
      urlParams: { userId },
      queryParams: {
        fields: 'FULL',
        usernameOrEmail: searchParams.text,
        page: searchParams.page ?? 0,
        count: searchParams.count ?? 10,
      },
    });
    return this.httpClient.get<OrderApprovalBuyerSearchResult>(url);
  }

  makeDecision(
    userId: string,
    orderNumber: string,
    orderApprovalDecision: OrderApprovalDecision
  ): Observable<OrderApprovalDecision> {
    const url = this.occEndpointsService.buildUrl('orderApproval', {
      urlParams: {
        userId,
        orderNumber,
      },
    });
    return this.httpClient.post(url, orderApprovalDecision).pipe(
      map(() => orderApprovalDecision),
      this.converterService.pipeable(ORDER_APPROVAL_DECISION_NORMALIZER)
    );
  }

  abortHijack(userId: string, cartId: string): Observable<Object> {
    return this.httpClient.delete(this.occEndpointsService.buildUrl('abortHijack', { urlParams: { userId, cartId } }));
  }

  recoverOrder(userId: string, orderNumber: string, cartId: string): Observable<Object> {
    return this.httpClient.patch(
      this.occEndpointsService.buildUrl('recoverOrder', {
        urlParams: {
          userId,
          orderNumber,
          cartId,
        },
      }),
      {}
    );
  }

  getOrderApprovalDetails(userId: string, orderNumber: string): Observable<OrderApproval> {
    const url = this.occEndpointsService.buildUrl('orderApprovalDetails', { urlParams: { userId, orderNumber } });
    return this.httpClient.get<Occ.OrderApproval>(url).pipe(this.converterService.pipeable(ORDER_APPROVAL_DETAILS_NORMALIZER));
  }

  deleteOrderApproval(userId: string, orderApprovalCode: string): Observable<any> {
    return this.httpClient.delete(
      this.occEndpointsService.buildUrl('orderDetails', {
        urlParams: {
          userId,
          orderCode: orderApprovalCode,
        },
      })
    );
  }
}
